import { type BlockConfig, iconField } from './_types';

/**
 * Payload `Block` config sidecar for the `threeStepFlow` block.
 *
 * Decomposed from `src/components/howitworks/ThreeStepFlow.tsx`. Two visual
 * variants accommodate `StepsSection` (5-step `numbered` variant) and
 * `HowPredictionsWork` (3-step `compact` variant).
 *
 * Slug + interfaceName naming reconciliation:
 *   File name:               `three-step-flow.tsx` / `three-step-flow.payload.ts`
 *   React component export:  `ThreeStepFlow`
 *   Payload `slug`:          `threeStepFlow`
 *   Payload `interfaceName`: `ThreeStepFlowBlock`
 */

export const ThreeStepFlowBlock: BlockConfig = {
  slug: 'threeStepFlow',
  interfaceName: 'ThreeStepFlowBlock',
  labels: {
    singular: 'Three Step Flow',
    plural: 'Three Step Flow Blocks',
  },
  fields: [
    {
      name: 'eyebrow',
      type: 'text',
      label: 'Eyebrow',
    },
    {
      name: 'headline',
      type: 'text',
      label: 'Headline',
      defaultValue: 'Three Simple Steps',
    },
    {
      name: 'subheadline',
      type: 'textarea',
      label: 'Subheadline',
      admin: { rows: 2 },
    },
    {
      name: 'variant',
      type: 'select',
      defaultValue: 'numbered',
      label: 'Variant',
      options: [
        { label: 'Numbered (default)', value: 'numbered' },
        { label: 'Compact (single muted card)', value: 'compact' },
      ],
    },
    {
      name: 'connectingLine',
      type: 'checkbox',
      defaultValue: true,
      label: 'Show connecting line (numbered variant)',
    },
    {
      name: 'steps',
      type: 'array',
      required: true,
      minRows: 2,
      maxRows: 8,
      label: 'Steps',
      labels: { singular: 'Step', plural: 'Steps' },
      fields: [
        iconField({
          name: 'icon',
          label: 'Icon (lucide-react name)',
        }),
        {
          name: 'number',
          type: 'text',
          label: 'Step number / label',
          admin: {
            description:
              'Optional override for the auto-generated step number. Useful for "01", "02"... formatting (matches the StepsSection variant).',
          },
        },
        { name: 'title', type: 'text', required: true, label: 'Title' },
        {
          name: 'description',
          type: 'textarea',
          label: 'Description',
          admin: { rows: 2 },
        },
        {
          name: 'accentColor',
          type: 'select',
          label: 'Accent color',
          options: [
            { label: 'Blue', value: 'blue' },
            { label: 'Purple', value: 'purple' },
            { label: 'Amber', value: 'amber' },
            { label: 'Green', value: 'green' },
          ],
        },
      ],
    },
  ],
};

export default ThreeStepFlowBlock;
